import { useEffect, useState, useRef, useCallback } from 'react';
import useApiFetch from '../../hooks/useApiFetch';
import { Modal, Typography, Checkbox, Tooltip, message, Spin, Tag } from 'antd';
import { Button } from '../buttons/buttons';
import FeatherIcon from 'feather-icons-react';
import { useAuth } from '../../context/auth.context';

const { Title, Text } = Typography;

// Utility function to load Razorpay script dynamically
const loadRazorpayScript = () => {
    return new Promise((resolve) => {
        if (window.Razorpay) {
            resolve(true);
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => {
            resolve(true);
        };
        script.onerror = () => {
            resolve(false);
        };
        document.body.appendChild(script);
    });
};

function UpdateSubscriptionModel({ mode = 'initial', onClose, visible }) {
    const { currentUser, apiClient } = useAuth();
    const modalType = 'update';

    // Pricing modal state
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [selectedAddon, setSelectedAddon] = useState({});
    const [loading, setLoading] = useState(false);
    const [userCurrency, setUserCurrency] = useState(currentUser?.organization?.preferredCurrency || 'INR');
    const [pollingForSubscription, setPollingForSubscription] = useState(false);
    const [addons, setAddons] = useState([]);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const plansScrollRef = useRef(null);

    // Loading modal state
    const [showLoadingModal, setShowLoadingModal] = useState(false);

    // Fetch plans data
    const {
        data: plans,
        loading: isLoading,
        refetch: fetchPricingPlans,
    } = useApiFetch('api/subscriptions/pricing-plans', (res) => res.data, true);

    useEffect(() => {
        if (visible && !plans) {
            fetchPricingPlans();
        }
    }, [visible, plans, fetchPricingPlans]);

    const plansWithPrices =
        plans?.filter((plan) => {
            return plan.prices && plan.prices.length > 0;
        }) || [];

    // Fetch addon data
    useEffect(() => {
        const fetchAddons = async () => {
            if (!selectedPlan) return;

            try {
                const res = await apiClient.get(`api/subscriptions/addons?planId=${selectedPlan}`);
                setAddons(res.data.data || []);
            } catch (err) {
                console.error('Failed to fetch addons:', err);
                setAddons([]);
            }
        };

        fetchAddons();
    }, [selectedPlan, apiClient]);

    useEffect(() => {
        if (!currentUser) return;

        const subscription = currentUser.orgSubscription;
        if (mode === 'update' && subscription?.planId) {
            setSelectedPlan(subscription.planId);
        }
    }, [currentUser, mode]);

    // Helper functions
    const getUserCurrency = () => {
        return userCurrency;
    };

    const getPriceForUser = (plan, strict = false) => {
        const preferredCurrency = getUserCurrency();
        const priceData = plan.prices.find((p) => p.currency === preferredCurrency);

        if (strict) {
            return priceData || null;
        }

        if (priceData) return priceData;

        const fallbackPrice = plan.prices.find((p) => p.currency === 'USD');
        if (fallbackPrice) return fallbackPrice;

        return plan.prices.length > 0 ? plan.prices[0] : null;
    };

    const formatPrice = (price, currency) => {
        const symbols = {
            INR: '₹',
            USD: '$',
            EUR: '€',
            SGD: 'S$',
        };
        return `${symbols[currency] || currency}${price}`;
    };

    // Filter plans based on selected currency
    const getFilteredPlans = () => {
        return plansWithPrices.filter((plan) => {
            return plan.prices.some((price) => price.currency === userCurrency);
        });
    };

    // Poll subscription status
    const pollSubscriptionStatus = useCallback((maxAttempts = 30, intervalSeconds = 5, oldPlanIdParam = null) => {
        let attempts = Number(localStorage.getItem('pollAttempts') || '0');
        let stopPolling = false;

        const savedPlanId =
            oldPlanIdParam !== null
                ? String(oldPlanIdParam || 'none')
                : localStorage.getItem('currentPlanId') || 'none';

        localStorage.setItem('isPolling', 'true');
        localStorage.setItem('currentPlanId', savedPlanId);

        setPollingForSubscription(true);
        setShowLoadingModal(true);

        const cleanupAndFinish = (success, msg) => {
            stopPolling = true;
            localStorage.removeItem('isPolling');
            localStorage.removeItem('pollAttempts');
            localStorage.removeItem('currentPlanId');
            setPollingForSubscription(false);
            setShowLoadingModal(false);

            if (success) {
                message.success('Subscription activated successfully! Reloading...');
                setTimeout(() => {
                    window.location.reload();
                }, 1000);
            } else {
                message.warning(msg || 'Subscription status check completed.');
            }
        };

        const poll = async () => {
            if (stopPolling) return;

            attempts++;
            localStorage.setItem('pollAttempts', String(attempts));

            try {
                const { data } = await apiClient.get('/api/org-users/me');
                const meUser = Array.isArray(data?.data) ? data.data[0] : data?.data;
                const currentPlanId = meUser?.orgSubscription?.planId ? String(meUser.orgSubscription.planId) : null;
                const expiryDate = meUser?.orgSubscription?.expiryDate;

                if (savedPlanId !== 'none' && currentPlanId && savedPlanId !== currentPlanId) {
                    cleanupAndFinish(true);
                    return;
                }

                if (savedPlanId === 'none' && currentPlanId) {
                    cleanupAndFinish(true);
                    return;
                }

                if (expiryDate && new Date(expiryDate) <= new Date()) {
                    cleanupAndFinish(false);
                    return;
                }

                if (attempts < maxAttempts) {
                    setTimeout(poll, intervalSeconds * 1000);
                } else {
                    cleanupAndFinish(false, 'Max attempts reached. Please refresh.');
                }
            } catch (err) {
                console.error('Error in polling:', err);
                if (attempts < maxAttempts) {
                    setTimeout(poll, intervalSeconds * 1000);
                } else {
                    cleanupAndFinish(false, 'Failed to check subscription status. Please refresh.');
                }
            }
        };

        poll();
    }, [apiClient]);

    useEffect(() => {
        const isPolling = localStorage.getItem('isPolling');
        const savedPlanId = localStorage.getItem('currentPlanId');

        if (isPolling === 'true' && savedPlanId) {
            setShowLoadingModal(true);
            setPollingForSubscription(true);
            pollSubscriptionStatus(30, 5, savedPlanId);
        }
    }, [pollSubscriptionStatus]);

    // Handle payment success
    const handlePaymentSuccess = async (paymentResponse, subscriptionDraft) => {
        setLoading(false);
        const oldPlanId = currentUser?.orgSubscription?.planId || 'none';
        pollSubscriptionStatus(30, 5, oldPlanId);
    };

    const handleUpdateAndProceedToPayment = async () => {
        if (!selectedPlan) {
            message.error('Please select a plan first');
            return;
        }

        try {
            setLoading(true);

            const scriptLoaded = await loadRazorpayScript();
            if (!scriptLoaded) {
                message.error('Failed to load payment system. Please try again.');
                setLoading(false);
                return;
            }

            const chosenPlan = plans.find((p) => p.id === selectedPlan);
            const userPriceData = getPriceForUser(chosenPlan);

            if (!userPriceData) {
                message.error('Pricing information not available for your currency');
                setLoading(false);
                return;
            }

            const payload = {
                planId: selectedPlan,
                pricingTierId: userPriceData.id,
                currency: userPriceData.currency,
                addons: (selectedAddon[selectedPlan] || []).map((addon) => ({
                    addonId: addon.id,
                    quantity: 1,
                })),
            };

            const response = await apiClient.post('/api/subscriptions/update-plan', payload);
            const data = response.data?.data[0];
            const subscriptionDraft = data?.subscriptionDraft;
            const razorpayPayment = data?.razorpayPayment;

            if (razorpayPayment && subscriptionDraft) {
                const oldPlanId = currentUser?.orgSubscription?.planId || 'none';
                localStorage.setItem('isPolling', 'true');
                localStorage.setItem('currentPlanId', String(oldPlanId));

                const options = {
                    key: process.env.REACT_APP_RAZORPAY_KEY_ID,
                    order_id: razorpayPayment.id,
                    amount: razorpayPayment.amount,
                    currency: razorpayPayment.currency,
                    name: 'CommKraft',
                    description: `${chosenPlan.name} Plan - Subscription`,
                    image: '/logo192.png',
                    handler: function (res) {
                        handlePaymentSuccess(res, subscriptionDraft);
                    },
                    prefill: {
                        name: `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim(),
                        email: currentUser?.email || '',
                        contact: currentUser?.phoneNumber || '',
                    },
                    notes: {
                        subscriptionDraftId: subscriptionDraft.id.toString(),
                        organizationId: currentUser?.organizationId?.toString(),
                        planId: selectedPlan.toString(),
                        pricingTierId: userPriceData.id.toString(),
                    },
                    theme: {
                        color: '#5F63F2',
                    },
                    modal: {
                        ondismiss: function () {
                            localStorage.removeItem('isPolling');
                            localStorage.removeItem('currentPlanId');
                            setLoading(false);
                        },
                    },
                    retry: {
                        enabled: true,
                        max_count: 3,
                    },
                };

                const rzp = new window.Razorpay(options);
                rzp.on('payment.failed', (resp) => {
                    console.error('Payment failed:', resp.error);
                    setLoading(false);
                    message.error('Payment failed. Please try again.');
                });

                rzp.open();
            } else {
                message.error('Failed to create payment session');
                setLoading(false);
            }
        } catch (error) {
            console.error('Error updating plan:', error);
            message.error('Failed to process payment. Please try again.');
            localStorage.removeItem('isPolling');
            setLoading(false);
        }
    };

    const handleAddonChange = (planId, newAddons) => {
        setSelectedAddon((prev) => ({
            ...prev,
            [planId]: newAddons,
        }));
    };

    // Prepare pricing data
    const filteredPlans = getFilteredPlans();
    const pricingCardData =
        filteredPlans &&
        filteredPlans.map((plan) => {
            const priceData = getPriceForUser(plan);
            return {
                id: plan.id,
                name: plan.name,
                description: plan.description,
                price: priceData ? formatPrice(priceData.price, priceData.currency) : 'N/A',
                billingCycle: priceData ? priceData?.billingCycle.toLowerCase() : '',
                currency: priceData ? priceData.currency : null,
                rawPrice: priceData ? priceData.price : 0,
                features: plan.features.map((f) => {
                    const limitText = f.type === 'limit' && f.limit ? ` - Limit: ${f.limit}` : '';
                    return `${f.feature.name}${limitText}`;
                }),
                addons: plan.features.flatMap(
                    (f) =>
                        f.feature.addons?.map((addon) => ({
                            id: addon.id,
                            name: `${f.feature.name} Addon – ${formatPrice(
                                addon.unitPrice,
                                priceData?.currency || 'USD',
                            )}`,
                            description: f.feature.description || 'No description available',
                            unitPrice: addon.unitPrice,
                            featureName: f.feature.name,
                        })) || [],
                ),
            };
        });

    const filteredAddons = addons.filter((addon) => getPriceForUser(addon, true));

    // Scroll handlers
    const checkScrollButtons = useCallback(() => {
        if (plansScrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = plansScrollRef.current;
            setCanScrollLeft(scrollLeft > 10);
            setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
        }
    }, []);

    const handlePlansScroll = (direction) => {
        if (plansScrollRef.current) {
            const scrollAmount = 330;
            plansScrollRef.current.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth',
            });
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            checkScrollButtons();
        }, 200);

        const currentRef = plansScrollRef.current;
        if (currentRef) {
            currentRef.addEventListener('scroll', checkScrollButtons);
        }
        window.addEventListener('resize', checkScrollButtons);

        return () => {
            clearTimeout(timer);
            if (currentRef) {
                currentRef.removeEventListener('scroll', checkScrollButtons);
            }
            window.removeEventListener('resize', checkScrollButtons);
        };
    }, [pricingCardData?.length, visible, checkScrollButtons]);

    // Loading Modal
    const LoadingModal = () => {
        const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
        const messages = [
            'Please wait while we process your request.',
            'Securing your data and preparing your dashboard...',
            'Almost there! Just a moment more...',
        ];

        useEffect(() => {
            if (!showLoadingModal) return;

            const interval = setInterval(() => {
                setCurrentMessageIndex((prev) => (prev + 1) % messages.length);
            }, 2500);

            return () => clearInterval(interval);
        }, [showLoadingModal]);

        return (
            <Modal
                open={showLoadingModal}
                centered
                closable={false}
                maskClosable={false}
                footer={null}
                zIndex={999999}
                width={420}
            >
                <div style={{ padding: '32px 20px', textAlign: 'center' }}>
                    <Spin size="large" style={{ marginBottom: 16 }} />
                    <Title level={4} style={{ margin: '0 0 8px 0', color: '#1E293B' }}>
                        Processing Request
                    </Title>
                    <Text type="secondary" style={{ fontSize: '14px', color: '#64748B' }}>
                        {messages[currentMessageIndex]}
                    </Text>
                </div>
            </Modal>
        );
    };

    return (
        <>
            <LoadingModal />

            {modalType === 'update' && (
                <Modal
                    open={visible}
                    centered
                    closable={true}
                    onCancel={onClose}
                    maskClosable={true}
                    footer={null}
                    zIndex={9999}
                    width={1060}
                    bodyStyle={{ padding: '28px 32px 32px' }}
                >
                    <div style={{ textAlign: 'center', marginBottom: 20 }}>
                        <h2
                            style={{
                                fontSize: '24px',
                                fontWeight: 800,
                                color: '#0F172A',
                                margin: '0 0 6px 0',
                                letterSpacing: '-0.5px',
                            }}
                        >
                            {mode === 'update' ? 'Update Your Subscription Plan' : 'Choose Your Ideal Plan'}
                        </h2>
                        <div style={{ fontSize: '14px', color: '#64748B', maxWidth: '520px', margin: '0 auto 16px' }}>
                            Select the plan that fits your business needs. Upgrade, downgrade, or switch billing at any time.
                        </div>

                        {/* Currency Selector Pill */}
                        <div
                            style={{
                                display: 'inline-flex',
                                background: '#F1F5F9',
                                padding: '4px',
                                borderRadius: '10px',
                                border: '1px solid #E2E8F0',
                            }}
                        >
                            {['INR', 'USD', 'EUR', 'SGD'].map((curr) => {
                                const symbols = { INR: '₹ INR', USD: '$ USD', EUR: '€ EUR', SGD: 'S$ SGD' };
                                const isSelected = userCurrency === curr;
                                return (
                                    <button
                                        key={curr}
                                        type="button"
                                        onClick={() => {
                                            setUserCurrency(curr);
                                            setSelectedPlan(null);
                                            setSelectedAddon({});
                                        }}
                                        style={{
                                            padding: '6px 16px',
                                            borderRadius: '8px',
                                            border: 'none',
                                            background: isSelected ? '#FFFFFF' : 'transparent',
                                            color: isSelected ? '#4F46E5' : '#64748B',
                                            fontWeight: isSelected ? 700 : 500,
                                            fontSize: '13px',
                                            boxShadow: isSelected ? '0 2px 4px rgba(0,0,0,0.06)' : 'none',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                        }}
                                    >
                                        {symbols[curr]}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <Spin spinning={isLoading}>
                        {pricingCardData && !isLoading && pricingCardData.length === 0 ? (
                            <div
                                style={{
                                    padding: '40px 20px',
                                    textAlign: 'center',
                                    background: '#F8FAFC',
                                    borderRadius: '10px',
                                    border: '1px dashed #CBD5E1',
                                }}
                            >
                                <FeatherIcon icon="alert-circle" size={36} color="#F59E0B" style={{ marginBottom: 12 }} />
                                <h4 style={{ color: '#1E293B', fontWeight: 600 }}>No plans available in {userCurrency}</h4>
                                <p style={{ color: '#64748B', margin: 0 }}>
                                    Please select another currency to view available plans.
                                </p>
                            </div>
                        ) : (
                            <>
                                {/* Plans Carousel Container */}
                                <div style={{ position: 'relative', marginBottom: 20 }}>
                                    {/* Left Navigation Arrow */}
                                    {canScrollLeft && (
                                        <button
                                            type="button"
                                            onClick={() => handlePlansScroll('left')}
                                            style={{
                                                position: 'absolute',
                                                left: -14,
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                zIndex: 20,
                                                width: 38,
                                                height: 38,
                                                borderRadius: '50%',
                                                background: '#FFFFFF',
                                                border: '1.5px solid #E2E8F0',
                                                boxShadow: '0 4px 14px rgba(0, 0, 0, 0.15)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                color: '#4F46E5',
                                                transition: 'all 0.2s ease',
                                            }}
                                            title="Scroll Left"
                                        >
                                            <FeatherIcon icon="chevron-left" size={22} />
                                        </button>
                                    )}

                                    {/* Right Navigation Arrow */}
                                    {canScrollRight && (
                                        <button
                                            type="button"
                                            onClick={() => handlePlansScroll('right')}
                                            style={{
                                                position: 'absolute',
                                                right: -14,
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                zIndex: 20,
                                                width: 38,
                                                height: 38,
                                                borderRadius: '50%',
                                                background: '#FFFFFF',
                                                border: '1.5px solid #E2E8F0',
                                                boxShadow: '0 4px 14px rgba(0, 0, 0, 0.15)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                color: '#4F46E5',
                                                transition: 'all 0.2s ease',
                                            }}
                                            title="Scroll Right"
                                        >
                                            <FeatherIcon icon="chevron-right" size={22} />
                                        </button>
                                    )}

                                    {/* Horizontal Scrollable Plans Row */}
                                    <div
                                        ref={plansScrollRef}
                                        style={{
                                            display: 'flex',
                                            overflowX: 'auto',
                                            gap: '18px',
                                            padding: '20px 6px 16px',
                                            scrollBehavior: 'smooth',
                                            scrollbarWidth: 'thin',
                                            alignItems: 'stretch',
                                        }}
                                    >
                                        {pricingCardData &&
                                            pricingCardData.map((plan, index) => {
                                                const isSelected = selectedPlan === plan.id;
                                                const isPopular =
                                                    plan.name?.toLowerCase().includes('enterprise') || index === 1;

                                                return (
                                                    <div
                                                        key={plan.id}
                                                        onClick={() => setSelectedPlan(plan.id)}
                                                        style={{
                                                            position: 'relative',
                                                            borderRadius: '10px',
                                                            border: isSelected ? '2.5px solid #5F63F2' : '1px solid #E2E8F0',
                                                            background: isSelected
                                                                ? 'linear-gradient(180deg, #FFFFFF 0%, #F5F7FF 100%)'
                                                                : '#FFFFFF',
                                                            boxShadow: isSelected
                                                                ? '0 12px 28px -6px rgba(95, 99, 242, 0.22)'
                                                                : '0 2px 8px rgba(0,0,0,0.04)',
                                                            padding: '24px 20px',
                                                            flex: pricingCardData.length <= 3 ? '1 1 0' : '0 0 310px',
                                                            minWidth: '290px',
                                                            maxWidth: pricingCardData.length <= 3 ? 'none' : '330px',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            justifyContent: 'space-between',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.25s ease',
                                                            transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                                                        }}
                                                    >
                                                        {isPopular && (
                                                            <div
                                                                style={{
                                                                    position: 'absolute',
                                                                    top: -12,
                                                                    left: '50%',
                                                                    transform: 'translateX(-50%)',
                                                                    background:
                                                                        'linear-gradient(135deg, #5F63F2 0%, #4338CA 100%)',
                                                                    color: '#FFFFFF',
                                                                    padding: '4px 14px',
                                                                    borderRadius: '20px',
                                                                    fontSize: '11px',
                                                                    fontWeight: 700,
                                                                    letterSpacing: '0.6px',
                                                                    textTransform: 'uppercase',
                                                                    boxShadow: '0 3px 8px rgba(95, 99, 242, 0.45)',
                                                                    zIndex: 10,
                                                                    whiteSpace: 'nowrap',
                                                                    lineHeight: '14px',
                                                                }}
                                                            >
                                                                POPULAR
                                                            </div>
                                                        )}

                                                        <div>
                                                            {/* Tier Title (No Radio Button) */}
                                                            <div
                                                                style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'space-between',
                                                                    marginBottom: 12,
                                                                }}
                                                            >
                                                                <span
                                                                    style={{
                                                                        fontSize: '16px',
                                                                        fontWeight: 800,
                                                                        textTransform: 'uppercase',
                                                                        letterSpacing: '0.6px',
                                                                        color: isSelected ? '#4F46E5' : '#1E293B',
                                                                    }}
                                                                >
                                                                    {plan.name}
                                                                </span>
                                                            </div>

                                                            {/* Price Display */}
                                                            <div
                                                                style={{
                                                                    paddingBottom: 16,
                                                                    borderBottom: '1px solid #F1F5F9',
                                                                    marginBottom: 16,
                                                                }}
                                                            >
                                                                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                                                                    <span
                                                                        style={{
                                                                            fontSize: '32px',
                                                                            fontWeight: 800,
                                                                            color: '#0F172A',
                                                                            letterSpacing: '-1px',
                                                                        }}
                                                                    >
                                                                        {plan.price}
                                                                    </span>
                                                                    {plan.billingCycle && (
                                                                        <span
                                                                            style={{
                                                                                fontSize: '13px',
                                                                                color: '#64748B',
                                                                                fontWeight: 500,
                                                                            }}
                                                                        >
                                                                            / {plan.billingCycle}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* Features list */}
                                                            <div
                                                                style={{
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    gap: 10,
                                                                    maxHeight: 230,
                                                                    overflowY: 'auto',
                                                                    paddingRight: 4,
                                                                }}
                                                            >
                                                                {plan.features.map((feature, fIdx) => (
                                                                    <div
                                                                        key={fIdx}
                                                                        style={{
                                                                            display: 'flex',
                                                                            alignItems: 'flex-start',
                                                                            gap: 8,
                                                                            fontSize: '13px',
                                                                            color: '#334155',
                                                                        }}
                                                                    >
                                                                        <div
                                                                            style={{
                                                                                width: 17,
                                                                                height: 17,
                                                                                borderRadius: '50%',
                                                                                background: '#ECFDF5',
                                                                                color: '#059669',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                flexShrink: 0,
                                                                                marginTop: 1,
                                                                            }}
                                                                        >
                                                                            <FeatherIcon icon="check" size={11} />
                                                                        </div>
                                                                        <span style={{ lineHeight: '18px', fontWeight: 500 }}>
                                                                            {feature}
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                            </div>

                                                            {/* Down Arrow indicator for more features */}
                                                            {plan.features.length > 5 && (
                                                                <div
                                                                    style={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        gap: 4,
                                                                        paddingTop: 8,
                                                                        fontSize: '11px',
                                                                        color: '#94A3B8',
                                                                        fontWeight: 600,
                                                                    }}
                                                                >
                                                                    <span>Scroll for more features</span>
                                                                    <FeatherIcon icon="chevron-down" size={13} />
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Select Button Indicator */}
                                                        <div style={{ marginTop: 16 }}>
                                                            <div
                                                                style={{
                                                                    width: '100%',
                                                                    padding: '10px 14px',
                                                                    borderRadius: '8px',
                                                                    textAlign: 'center',
                                                                    fontSize: '13px',
                                                                    fontWeight: 600,
                                                                    background: isSelected
                                                                        ? 'linear-gradient(135deg, #5F63F2 0%, #4F46E5 100%)'
                                                                        : '#F8FAFC',
                                                                    color: isSelected ? '#FFFFFF' : '#475569',
                                                                    border: isSelected ? 'none' : '1px solid #E2E8F0',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    gap: 6,
                                                                    boxShadow: isSelected
                                                                        ? '0 4px 10px rgba(95, 99, 242, 0.25)'
                                                                        : 'none',
                                                                    transition: 'all 0.2s ease',
                                                                }}
                                                            >
                                                                {isSelected && <FeatherIcon icon="check-circle" size={14} />}
                                                                <span>{isSelected ? 'Selected Plan' : 'Select Plan'}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                    </div>
                                </div>

                                {/* Add-ons Section */}
                                {filteredAddons.length > 0 && (
                                    <div
                                        style={{
                                            background: '#F8FAFC',
                                            borderRadius: '10px',
                                            border: '1px solid #E2E8F0',
                                            padding: '16px 20px',
                                            marginBottom: 24,
                                            textAlign: 'left',
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                            <FeatherIcon icon="plus-circle" size={16} color="#5F63F2" />
                                            <span style={{ fontSize: '14px', fontWeight: 700, color: '#1E293B' }}>
                                                Available Add-ons
                                            </span>
                                        </div>

                                        <div
                                            style={{
                                                display: 'grid',
                                                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                                                gap: 10,
                                            }}
                                        >
                                            {filteredAddons.map((addon) => {
                                                const { id: addonId, feature } = addon;
                                                const featureName = feature?.name || 'Unknown Feature';
                                                const featureDescription = feature?.description || '';
                                                const priceData = getPriceForUser(addon, true);
                                                const isChecked = (selectedAddon[selectedPlan] || []).some(
                                                    (item) => item.id === addonId,
                                                );

                                                return (
                                                    <div
                                                        key={`addon-${addonId}`}
                                                        onClick={() => {
                                                            if (!selectedPlan) {
                                                                message.info('Please select a plan first');
                                                                return;
                                                            }
                                                            const current = selectedAddon[selectedPlan] || [];
                                                            const next = !isChecked
                                                                ? [...current, addon]
                                                                : current.filter((item) => item.id !== addonId);
                                                            handleAddonChange(selectedPlan, next);
                                                        }}
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            padding: '10px 14px',
                                                            background: isChecked ? '#EEF2FF' : '#FFFFFF',
                                                            border: isChecked ? '1.5px solid #5F63F2' : '1px solid #E2E8F0',
                                                            borderRadius: '8px',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s ease',
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                            <Checkbox checked={isChecked} onChange={() => {}} />
                                                            <span style={{ fontSize: '13px', fontWeight: 600, color: '#1E293B', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                                                {featureName}
                                                                {featureDescription && (
                                                                    <Tooltip
                                                                        title={featureDescription}
                                                                        placement="topLeft"
                                                                        zIndex={99999}
                                                                    >
                                                                        <FeatherIcon
                                                                            icon="info"
                                                                            size={14}
                                                                            color="#94A3B8"
                                                                            style={{ cursor: 'pointer' }}
                                                                        />
                                                                    </Tooltip>
                                                                )}
                                                            </span>
                                                        </div>
                                                        <Tag color="purple" style={{ margin: 0, fontWeight: 700, borderRadius: 4 }}>
                                                            {formatPrice(priceData.unitPrice, priceData.currency)}
                                                        </Tag>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {/* Modal Footer Controls */}
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                alignItems: 'center',
                                gap: 12,
                                borderTop: '1px solid #F1F5F9',
                                paddingTop: 18,
                            }}
                        >
                            <Button
                                type="light"
                                size="default"
                                onClick={onClose}
                                disabled={loading || pollingForSubscription}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                size="default"
                                onClick={handleUpdateAndProceedToPayment}
                                loading={loading}
                                disabled={!selectedPlan || pollingForSubscription || (pricingCardData && pricingCardData.length === 0)}
                            >
                                {mode === 'update'
                                    ? 'Update Plan'
                                    : pollingForSubscription
                                        ? 'Activating Subscription...'
                                        : 'Proceed to Payment'}
                            </Button>
                        </div>
                    </Spin>
                </Modal>
            )}
        </>
    );
}

export default UpdateSubscriptionModel;
